"use client";

import { useState, useEffect } from "react";
import { X, Play, CheckCircle, Loader } from "lucide-react";

export const CourseStructureModal = ({
  isOpen,
  onClose,
  courseData,
  playlistData,
  topicsData,
  onConfirm,
  isCreating,
}) => {
  const [structuredCourse, setStructuredCourse] = useState(null);

  useEffect(() => {
    if (topicsData && playlistData) {
      const matchedStructure = matchVideosToStructure(topicsData, playlistData);
      setStructuredCourse(matchedStructure);
    }
  }, [topicsData, playlistData]);

  const matchVideosToStructure = (topics, videos) => {
    return topics.map((topic) => ({
      title: topic.title,
      subtopics: topic.subtopics.map((subtopic) => {
        const matchingVideo = videos.find((v) => v.title === subtopic.title);
        return {
          title: subtopic.title,
          video: matchingVideo || null,
        };
      }),
    }));
  };

const normalize = (s) => s.trim().toLowerCase();
const getUnmatchedVideos = () => {
  const normalize = (s) => s.toLowerCase().replace(/\s+/g, "");

  const matchedTitles = structuredCourse
    ?.flatMap((topic) => topic.subtopics.map((s) => normalize(s.title))) || [];

  return playlistData?.filter((video) => !matchedTitles.includes(normalize(video.title))) || [];
};


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Course Structure Preview</h2>
            <p className="text-gray-600 mt-1">{courseData?.title}</p>
          </div>
          <button
            onClick={onClose}
            disabled={isCreating}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {!structuredCourse ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-blue-600 mr-3" />
              <span className="text-gray-600">Processing course structure...</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">Course Overview</h3>
                <p className="text-blue-800 text-sm">{courseData?.description}</p>
                <div className="mt-3 flex items-center space-x-4 text-sm text-blue-700">
                  <span>📹 {playlistData?.length || 0} videos</span>
                  <span>📚 {structuredCourse?.length || 0} chapters</span>
                </div>
              </div>

              {structuredCourse.map((topic, topicIndex) => (
                <div key={topicIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h4 className="font-medium text-gray-900">
                      Chapter {topicIndex + 1}: {topic.title}
                    </h4>
                  </div>
                  <div className="p-4 space-y-3">
                    {topic.subtopics.map((subtopic, subtopicIndex) => (
                      <div
                        key={subtopicIndex}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {subtopic.video ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{subtopic.title}</p>
                            {subtopic.video && (
                              <p className="text-sm text-gray-600 flex items-center mt-1">
                                <Play className="h-3 w-3 mr-1" />
                                {subtopic.video.title}
                              </p>
                            )}
                          </div>
                        </div>

                        {subtopic.video && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">YouTube</span>
                            <span className="text-xs text-gray-500">{subtopic.video.duration || "N/A"}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {getUnmatchedVideos().length > 0 && (
                <div className="border border-yellow-200 rounded-lg bg-yellow-50 p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Unmatched Videos</h4>
                  <p className="text-yellow-800 text-sm mb-3">
                    These videos couldn't be automatically matched to topics:
                  </p>
                  <div className="space-y-2">
                    {getUnmatchedVideos().map((video, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-yellow-800">
                        <Play className="h-3 w-3" />
                        <span>{video.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isCreating}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(structuredCourse)}
            disabled={isCreating || !structuredCourse}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:bg-blue-400"
          >
            {isCreating ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Creating Course...
              </>
            ) : (
              "Create Course"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
